// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  Statistic,
  Segment,
  Grid,
  Table,
  Button,
  Icon,
  Message,
  Loader,
  Card,
  Label
} from 'semantic-ui-react';
import { emailNotificationsAdminApi } from '../api/emailNotificationsAdminApi';
import {
  EmailNotificationStats,
  DailyStats,
  BrokerageStats,
  NotificationType
} from '../types/emailNotifications';

function EmailNotificationDashboard() {
  const [overallStats, setOverallStats] = useState<EmailNotificationStats | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [brokerageStats, setBrokerageStats] = useState<BrokerageStats[]>([]);
  const [typeStats, setTypeStats] = useState<Record<NotificationType, number> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all dashboard data in parallel
      const [overall, daily, brokerage, type] = await Promise.all([
        emailNotificationsAdminApi.getOverallStats(),
        emailNotificationsAdminApi.getDailyStats(),
        emailNotificationsAdminApi.getBrokerageStats(),
        emailNotificationsAdminApi.getTypeStats()
      ]);

      setOverallStats(overall);
      setDailyStats(daily);
      setBrokerageStats(brokerage);
      setTypeStats(type);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getNotificationTypeLabel = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.ADVISOR_PROGRESS:
        return 'Advisor Progress';
      case NotificationType.ADVISOR_COMPLETION:
        return 'Advisor Completion';
      case NotificationType.PROVIDER_NOTIFICATION:
        return 'Provider Notification';
      default:
        return type;
    }
  };

  const formatSuccessRate = (rate: number): string => {
    return `${rate.toFixed(1)}%`;
  };

  const getSuccessRateColor = (rate: number): 'green' | 'yellow' | 'red' => {
    if (rate >= 95) return 'green';
    if (rate >= 90) return 'yellow';
    return 'red';
  };

  // Sort brokerage stats by success rate (lowest first to identify issues)
  const sortedBrokerageStats = [...brokerageStats].sort(
    (a, b) => a.successRate - b.successRate
  );

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as='h1'>
        <Icon name='envelope' />
        <Header.Content>
          Email Notification Monitoring
          <Header.Subheader>
            Monitor email delivery status, statistics, and troubleshoot failed notifications
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading ? (
        <Loader active inline='centered'>
          Loading dashboard data...
        </Loader>
      ) : (
        <>
          {/* Overall Statistics Cards */}
          {overallStats && (
            <Segment>
              <Header as='h2'>
                <Icon name='chart bar' />
                Overall Statistics
              </Header>
              <Statistic.Group widths='four'>
                <Statistic color='green'>
                  <Statistic.Value>
                    <Icon name='check circle' />
                    {overallStats.totalSent}
                  </Statistic.Value>
                  <Statistic.Label>Sent</Statistic.Label>
                </Statistic>

                <Statistic color='red'>
                  <Statistic.Value>
                    <Icon name='times circle' />
                    {overallStats.totalFailed}
                  </Statistic.Value>
                  <Statistic.Label>Failed</Statistic.Label>
                </Statistic>

                <Statistic color='yellow'>
                  <Statistic.Value>
                    <Icon name='clock' />
                    {overallStats.totalPending}
                  </Statistic.Value>
                  <Statistic.Label>Pending</Statistic.Label>
                </Statistic>

                <Statistic color='blue'>
                  <Statistic.Value>
                    <Icon name='percent' />
                    {formatSuccessRate(overallStats.successRate)}
                  </Statistic.Value>
                  <Statistic.Label>Success Rate</Statistic.Label>
                </Statistic>
              </Statistic.Group>

              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <small style={{ color: '#666' }}>
                  Average Retry Count: {overallStats.avgRetryCount.toFixed(2)}
                </small>
              </div>
            </Segment>
          )}

          {/* Notification Type Distribution */}
          {typeStats && (
            <Segment>
              <Header as='h2'>
                <Icon name='pie chart' />
                Notification Type Distribution
              </Header>
              <Card.Group itemsPerRow={3}>
                {Object.entries(typeStats).map(([type, count]) => (
                  <Card key={type}>
                    <Card.Content>
                      <Card.Header>
                        <Icon name='mail' />
                        {getNotificationTypeLabel(type as NotificationType)}
                      </Card.Header>
                      <Card.Meta>
                        <Statistic size='small'>
                          <Statistic.Value>{count}</Statistic.Value>
                          <Statistic.Label>notifications</Statistic.Label>
                        </Statistic>
                      </Card.Meta>
                    </Card.Content>
                  </Card>
                ))}
              </Card.Group>
            </Segment>
          )}

          {/* Daily Trend (Last 7 Days) */}
          {dailyStats.length > 0 && (
            <Segment>
              <Header as='h2'>
                <Icon name='line graph' />
                Recent Activity (Last 7 Days)
              </Header>
              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Date</Table.HeaderCell>
                    <Table.HeaderCell>Sent</Table.HeaderCell>
                    <Table.HeaderCell>Failed</Table.HeaderCell>
                    <Table.HeaderCell>Pending</Table.HeaderCell>
                    <Table.HeaderCell>Total</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {dailyStats.slice(0, 7).map((stat) => (
                    <Table.Row key={stat.date}>
                      <Table.Cell>{stat.date}</Table.Cell>
                      <Table.Cell>
                        <Label color='green'>{stat.sent}</Label>
                      </Table.Cell>
                      <Table.Cell>
                        <Label color='red'>{stat.failed}</Label>
                      </Table.Cell>
                      <Table.Cell>
                        <Label color='yellow'>{stat.pending}</Label>
                      </Table.Cell>
                      <Table.Cell>
                        <Label>{stat.total}</Label>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Segment>
          )}

          {/* Brokerage Performance */}
          {sortedBrokerageStats.length > 0 && (
            <Segment>
              <Header as='h2'>
                <Icon name='building' />
                Brokerage Performance
                <Header.Subheader>
                  Sorted by success rate (lowest first)
                </Header.Subheader>
              </Header>
              <Table celled striped sortable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Brokerage</Table.HeaderCell>
                    <Table.HeaderCell>Sent</Table.HeaderCell>
                    <Table.HeaderCell>Failed</Table.HeaderCell>
                    <Table.HeaderCell>Total</Table.HeaderCell>
                    <Table.HeaderCell>Success Rate</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {sortedBrokerageStats.map((stat) => (
                    <Table.Row
                      key={stat.brokerageId}
                      warning={stat.successRate < 90}
                      error={stat.successRate < 80}
                    >
                      <Table.Cell>
                        <Icon name='building' />
                        {stat.brokerageName}
                      </Table.Cell>
                      <Table.Cell>
                        <Label color='green' size='small'>
                          {stat.sent}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        <Label color='red' size='small'>
                          {stat.failed}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        <Label size='small'>{stat.total}</Label>
                      </Table.Cell>
                      <Table.Cell>
                        <Label
                          color={getSuccessRateColor(stat.successRate)}
                        >
                          {formatSuccessRate(stat.successRate)}
                        </Label>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Segment>
          )}

          {/* Action Buttons */}
          <Grid columns={2} stackable>
            <Grid.Column>
              <Button
                primary
                size='large'
                fluid
                onClick={() => navigate('/email-notifications/list')}
              >
                <Icon name='list' />
                View All Notifications
              </Button>
            </Grid.Column>
            <Grid.Column>
              <Button
                color='teal'
                size='large'
                fluid
                onClick={loadDashboardData}
              >
                <Icon name='refresh' />
                Refresh Dashboard
              </Button>
            </Grid.Column>
          </Grid>
        </>
      )}
    </Container>
  );
}

export default EmailNotificationDashboard;
